import type { IconName } from '@/shared/ui';
import { features } from './env';
import { routes } from './routes';

/** Every screen that can appear in the header title, tabs or navigation. */
export type PageKey =
  | 'practiceDashboard'
  | 'practiceReport'
  | 'appointmentStatistics'
  | 'newPatientStatistics'
  | 'revenueAnalysis'
  | 'utilization'
  | 'patientMap'
  | 'versionHistory'
  | 'users'
  | 'userAdd'
  | 'userEdit'
  | 'tickets'
  | 'ticketCreate'
  | 'ticketDetail'
  | 'whatsappLogs'
  | 'botConfiguration'
  | 'loginHistory'
  | 'profile'
  | 'analyticsCalls'
  | 'analyticsAppointmentTracking'
  | 'analyticsLocationSite'
  | 'analyticsReviews';

/**
 * Who may see a navigation entry. Evaluated by the session entity (`canAccess`):
 * - `all`: every signed-in user
 * - `admin`: ADMIN, SUPERADMIN, RPA
 * - `botConfiguration`: admins, or users with the botConfiguration permission
 */
export type AccessRule = 'all' | 'admin' | 'botConfiguration';

export interface NavItem {
  key: PageKey;
  path: string;
  icon: IconName;
  access: AccessRule;
}

export interface NavGroup {
  key: 'overview' | 'analytics' | 'operations' | 'marketing';
  items: NavItem[];
}

/** Metadata attached to routes (`handle`) for the header title and tabs. */
export interface RouteHandle {
  page: PageKey;
  icon: IconName;
}

// Grouping and order follow the client's prototype.
export const navGroups: NavGroup[] = [
  {
    key: 'overview',
    items: [
      { key: 'practiceDashboard', path: routes.practiceDashboard, icon: 'chart', access: 'all' },
      {
        key: 'appointmentStatistics',
        path: routes.appointmentStatistics,
        icon: 'calendar',
        access: 'all',
      },
      {
        key: 'newPatientStatistics',
        path: routes.newPatientStatistics,
        icon: 'usersplus',
        access: 'all',
      },
    ],
  },
  {
    key: 'analytics',
    items: [
      { key: 'revenueAnalysis', path: routes.revenueAnalysis, icon: 'receipt', access: 'all' },
      { key: 'patientMap', path: routes.patientMap, icon: 'map', access: 'all' },
      { key: 'versionHistory', path: routes.versionHistory, icon: 'history', access: 'all' },
    ],
  },
  {
    key: 'operations',
    items: [
      { key: 'users', path: routes.users, icon: 'user', access: 'admin' },
      { key: 'tickets', path: routes.tickets, icon: 'ticket', access: 'all' },
      { key: 'whatsappLogs', path: routes.whatsappLogs, icon: 'message', access: 'all' },
      {
        key: 'botConfiguration',
        path: routes.botConfiguration,
        icon: 'bot',
        access: 'botConfiguration',
      },
    ],
  },
  ...(features.marketingAnalytics
    ? [
        {
          key: 'marketing',
          items: [
            { key: 'analyticsCalls', path: routes.analyticsCalls, icon: 'phone', access: 'all' },
            {
              key: 'analyticsAppointmentTracking',
              path: routes.analyticsAppointmentTracking,
              icon: 'funnel',
              access: 'all',
            },
            {
              key: 'analyticsLocationSite',
              path: routes.analyticsLocationSite,
              icon: 'globe',
              access: 'all',
            },
            { key: 'analyticsReviews', path: routes.analyticsReviews, icon: 'star', access: 'all' },
          ],
        } satisfies NavGroup,
      ]
    : []),
];
